import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { Drawer, IconButton } from "@outpave/ds";
import moment from "moment";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  LinkBackwardIcon,
  MoreVerticalIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import { Chip, Menu, MenuItem, StatusGlyph } from "@outpave/ds";
import type { ChipTone } from "@outpave/ds";
import RenderSubscriptionStatus from "components/common/RenderSubscriptionStatus";
import { formatCashFull } from "utils/formatCash";
import { SubscriptionCanceledBy, SubscriptionStatus } from "enums/Subscription";
import {
  PaymentLinkSubscription,
  useSubscriptionData,
} from "hooks/useSubscriptionData";
import CancelSubscriptionModal from "./CancelSubscriptionModal";
import { intervalCadence } from "utils/billingInterval";

interface SubscriptionDetailsDrawerProps {
  open: boolean;
  subscriptionId: string;
  readOnly: boolean;
  onClose: (refresh?: boolean) => void;
}

const SubscriptionDetailsDrawer: React.FC<SubscriptionDetailsDrawerProps> = ({
  open,
  subscriptionId,
  readOnly,
  onClose,
}) => {
  const { t } = useTranslation();
  const {
    subscription,
    charges,
    loadSubscription,
    revokeManageLinks,
    loading,
  } = useSubscriptionData();
  const [cancelOpen, setCancelOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (open && subscriptionId) {
      loadSubscription(subscriptionId);
    }
  }, [open, subscriptionId, loadSubscription]);

  const canCancel =
    !readOnly &&
    subscription &&
    subscription.status !== SubscriptionStatus.CANCELED;

  return (
    <Drawer
      open={open}
      onClose={() => onClose()}
      loading={loading}
      title={[
        t("Subscription"),
        subscription?.payerName || subscription?.payerEmail || "",
      ]}
      headerAction={
        subscription ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "var(--space-2)",
            }}
          >
            {!readOnly && (
              <>
                <IconButton
                  variant="ghost"
                  aria-label={t("More actions")}
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                  <HugeiconsIcon icon={MoreVerticalIcon} size={20} />
                </IconButton>
                <Menu
                  anchorEl={anchorEl}
                  open={!!anchorEl}
                  onClose={() => setAnchorEl(null)}
                >
                  <MenuItem
                    leading={
                      <HugeiconsIcon icon={LinkBackwardIcon} size={20} />
                    }
                    onClick={() => {
                      setAnchorEl(null);
                      revokeManageLinks(subscription.id);
                    }}
                  >
                    {t("Revoke manage links")}
                  </MenuItem>
                </Menu>
              </>
            )}
          </Box>
        ) : undefined
      }
      footer={
        canCancel ? (
          <Box
            sx={{ display: "flex", justifyContent: "center", width: "100%" }}
          >
            <Button
              variant="danger-outlined"
              onClick={() => setCancelOpen(true)}
            >
              {t("Cancel Subscription")}
            </Button>
          </Box>
        ) : undefined
      }
    >
      <>
        {subscription && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "var(--space-5)",
            }}
          >
            <Paper sx={{ padding: "var(--space-5)" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-4)",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "var(--space-3)",
                  }}
                >
                  <Typography variant="h6">
                    {subscription.paymentLink?.name || t("Subscription")}
                  </Typography>
                  <RenderSubscriptionStatus
                    status={subscription.status}
                    awaitingPaymentMethod={subscription.awaitingPaymentMethod}
                  />
                </Box>

                <Typography variant="h4">
                  {formatCashFull(
                    subscription.chargeAmount,
                    subscription.currency,
                  )}
                </Typography>

                <Typography variant="body2" color="var(--color-text-secondary)">
                  {describeCadence(subscription, t)}
                </Typography>
              </Box>
            </Paper>

            <Paper sx={{ padding: "var(--space-5)" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-3)",
                }}
              >
                {subscription.paymentLinkId && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: "var(--space-4)",
                    }}
                  >
                    <Typography variant="subtitle2">
                      {t("Payment link")}
                    </Typography>
                    <Link
                      component="button"
                      type="button"
                      variant="body2"
                      onClick={() =>
                        navigate(
                          `/admin/payment-links?id=${subscription.paymentLinkId}`,
                        )
                      }
                      sx={{ textAlign: "right" }}
                    >
                      {subscription.paymentLink?.name || t("View")}
                    </Link>
                  </Box>
                )}

                {detailRows(subscription, t).map((row) => (
                  <Box
                    key={row.label}
                    sx={{
                      display: "flex",
                      alignItems: "baseline",
                      justifyContent: "space-between",
                      gap: "var(--space-4)",
                    }}
                  >
                    <Typography variant="subtitle2">{row.label}</Typography>
                    <Typography
                      variant="body2"
                      color="var(--color-text-secondary)"
                      sx={{ textAlign: "right" }}
                    >
                      {row.value}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Paper>

            <Paper sx={{ padding: "var(--space-5)" }}>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-3)",
                }}
              >
                <Typography variant="subtitle2">
                  {t("Charge history")}
                </Typography>
                {charges.length ? (
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "var(--space-1)",
                    }}
                  >
                    {charges.map((charge) => (
                      <Box
                        key={charge.id}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: "var(--space-3)",
                          paddingY: "var(--space-2)",
                        }}
                      >
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="subtitle2">
                            {charge.createdAt
                              ? moment(charge.createdAt).format("ll")
                              : moment(charge.period).format("ll")}
                          </Typography>
                          {charge.attempt > 1 && (
                            <Typography
                              variant="caption"
                              color="var(--color-text-tertiary)"
                            >
                              {t("AttemptNumber", { count: charge.attempt })}
                            </Typography>
                          )}
                        </Box>

                        <Typography variant="subtitle2">
                          {formatCashFull(charge.amount, charge.currency)}
                        </Typography>

                        <RenderChargeStatus status={charge.status} />
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography
                    variant="body2"
                    color="var(--color-text-tertiary)"
                  >
                    {t("No charges yet")}
                  </Typography>
                )}
              </Box>
            </Paper>
          </Box>
        )}

        <CancelSubscriptionModal
          open={cancelOpen}
          subscription={subscription}
          onClose={() => setCancelOpen(false)}
          onDone={() => onClose(true)}
        />
      </>
    </Drawer>
  );
};

function describeCadence(
  subscription: PaymentLinkSubscription,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const cadence = t(intervalCadence(subscription.interval));

  return subscription.nextBillingDate
    ? t("CadenceNextBilling", {
        cadence,
        date: moment(subscription.nextBillingDate).format("ll"),
      })
    : cadence;
}

const CHARGE_TONES: Record<string, { tone: ChipTone; glyph: string }> = {
  SUCCEEDED: { tone: "positive", glyph: "active" },
  PROCESSING: { tone: "info", glyph: "pending" },
  PENDING: { tone: "info", glyph: "pending" },
  FAILED: { tone: "negative", glyph: "error" },
};

const RenderChargeStatus: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  const { tone, glyph } = CHARGE_TONES[status] || {
    tone: "neutral" as ChipTone,
    glyph: "inactive",
  };

  return (
    <Chip
      size="sm"
      tone={tone}
      label={t(status)}
      leading={<StatusGlyph status={glyph} tone={tone} />}
    />
  );
};

/* SummaryComponent omits rows with falsy values. */
function detailRows(
  subscription: PaymentLinkSubscription,
  t: (key: string, options?: Record<string, unknown>) => string,
): { label: string; value: string }[] {
  return [
    {
      label: t("Subscriber"),
      value: subscription.payerName || subscription.payerEmail,
    },
    { label: t("Email"), value: subscription.payerEmail },
    {
      label: t("Includes covered fee"),
      value: subscription.feeCoverAmount
        ? formatCashFull(subscription.feeCoverAmount, subscription.currency)
        : "",
    },
    {
      label: t("Next billing"),
      value: subscription.nextBillingDate
        ? moment(subscription.nextBillingDate).format("ll")
        : t("Not scheduled"),
    },
    {
      label: t("Payment method"),
      value: subscription.paymentMethodSummary?.last4
        ? `${subscription.paymentMethodSummary.brand || ""} ****${subscription.paymentMethodSummary.last4}`
        : t("None saved"),
    },
    {
      label: t("Started"),
      value: subscription.createdAt
        ? moment(subscription.createdAt).format("ll")
        : "",
    },
    {
      label: t("Consent recorded"),
      value: subscription.consentAt
        ? moment(subscription.consentAt).format("lll")
        : "",
    },
    {
      label: t("Failed payments"),
      value: subscription.failedAttempts
        ? `${subscription.failedAttempts}${
            subscription.lastDeclineCode
              ? ` (${subscription.lastDeclineCode})`
              : ""
          }`
        : "",
    },
    {
      label: t("Cancellation reason"),
      value:
        subscription.canceledBy === SubscriptionCanceledBy.PAYMENT_FAILED
          ? t("Payment failed")
          : subscription.cancelReason || "",
    },
    {
      label: t("Scheduled to end"),
      value: subscription.cancelAtPeriodEnd ? t("After the paid period") : "",
    },
  ].filter((row) => !!row.value);
}

export default SubscriptionDetailsDrawer;
